import circle from '@disy/cadenza-icons/circle.svg';
import square from '@disy/cadenza-icons/square.svg';
import poly from '@disy/cadenza-icons/polygon.svg';
import line from '@disy/cadenza-icons/line.svg';
import point from '@disy/cadenza-icons/point.svg';

import type { SimpleGeometryType } from 'cadenza/admin/analytics-extensions/model/simple-geometry-type';
import { type Icon, icon, type IconOptions } from 'cadenza/utils/icon/icon';

import type { Type } from 'ol/geom/Geometry';

import i18n from './geometry-type.properties';

/** @deprecated Use the type instead */
export const GeometryType: Record<string, GeometryType> = {
  LINE: 'Line', // a LineString with maximal 2 Points
  OVAL: 'Oval',
  RECTANGLE: 'Rectangle',
  // Default OL types copied from ol/geom/GeometryType (except GeometryCollection unused for now)
  POINT: 'Point',
  LINE_STRING: 'LineString',
  LINEAR_RING: 'LinearRing',
  POLYGON: 'Polygon',
  MULTI_POINT: 'MultiPoint',
  MULTI_LINE_STRING: 'MultiLineString',
  MULTI_POLYGON: 'MultiPolygon',
  CIRCLE: 'Circle',
  GEOMETRY_COLLECTION: 'GeometryCollection'
} as const;

export type GeometryType = Type;
export type MultiGeometryType = Extract<GeometryType, 'MultiPoint' | 'MultiLineString' | 'MultiPolygon'>;

// incomplete, add more icons when needed
const ICON_BY_GEOMETRY_TYPE: Record<GeometryType, string> = {
  Circle: circle,
  Rectangle: square,
  Polygon: poly,
  MultiPolygon: poly,
  LineString: line,
  Point: point,
  // Add icons for all geometry types (even if not used yet) to please TS
  Oval: circle,
  LinearRing: poly,
  MultiLineString: line,
  Line: line,
  MultiPoint: point,
  GeometryCollection: poly
} as const;

export function isMultiGeometryType (geometryType: GeometryType) {
  return [ 'MultiPoint', 'MultiLineString', 'MultiPolygon' ].includes(geometryType);
}

export function getBaseGeometryType (geometryType: GeometryType): GeometryType {
  return geometryType.replace('Multi', '') as GeometryType;
}

export function getPrintName (geometryType: GeometryType): string {
  return i18n(geometryType);
}

export function getIcon (geometryType: GeometryType, options?: string | IconOptions): Icon {
  return icon(ICON_BY_GEOMETRY_TYPE[geometryType], options);
}

export function fromSimpleGeometryType (simpleGeometryType: SimpleGeometryType): GeometryType {
  switch (simpleGeometryType) {
    case 'POINT':
      return 'Point';
    case 'LINESTRING':
      return 'LineString';
    case 'LINEARRING':
      return 'LinearRing';
    case 'POLYGON':
      return 'Polygon';
    case 'MULTIPOINT':
      return 'MultiPoint';
    case 'MULTILINESTRING':
      return 'MultiLineString';
    case 'MULTIPOLYGON':
      return 'MultiPolygon';
    default:
      throw new Error(`${simpleGeometryType} can't be mapped to any of ${Object.values(GeometryType)}`);
  }
}

export const GEOMETRIES_SUPPORTING_BUFFER = [
  GeometryType.LINE_STRING,
  GeometryType.POINT,
  GeometryType.RECTANGLE,
  GeometryType.POLYGON,
  GeometryType.GEOMETRY_COLLECTION
];
